import { Box, Checkbox } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { materialStateEnum, stepModeEnum } from '../../constants';
import { useCreateDailyRentalMutation, useDailyRentalMutation, useDeleteDailyRentalMutation } from '../../hooks/mutations/useDailyRentalMutation';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyRental } from '../../services/apiSLTP/dailyReports/dailyRentals';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import ManageDailyRentalModal from '../modals/operations/planning/ManageDailyRentalModal';
import IconButton from '../ui/IconButton';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface RentalsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
    createTransfer?: boolean;
}

const RentalsCell: React.FC<RentalsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
    createTransfer = true,
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.RENTALS;

    const { mutate: updateDailyRental } = useDailyRentalMutation({ onSuccess: refetch });
    const { mutate: deleteDailyRental } = useDeleteDailyRentalMutation({ onSuccess: refetch });
    const { mutate: createDailyRental } = useCreateDailyRentalMutation({ onSuccess: refetch });

    const { selectedItem, manageModal, handleMove, handleDelete, handleDuplicate, openModal } = useDailyResource<DailyRental>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyRental({ id: itemId, body: { activityId: toLineId, createTransfer: true }, queries: { deleteTransfers: true } });
        },
        onDelete: async (itemId) => {
            deleteDailyRental({ id: itemId, queries: { deleteTransfers: true } });
        },
        onDuplicate: async (rental) => {
            createDailyRental({
                body: {
                    activityId: activity._id,
                    rentalTypeId: rental.rentalTypeId._id,
                    designation: rental.designation,
                    state: rental.state,
                    createTransfer,
                    price: rental.price,
                    priceType: rental.priceType,
                },
            });
        },
    });

    const countDuplicates = (rentalDesignation: string, companyId: string) => {
        const matches = activities
            .flatMap((a) => a.dailyRentals)
            .filter((r) => r.designation.toLowerCase() === rentalDesignation.toLowerCase() && r.rentalTypeId._id === companyId);

        if (matches.length === 1) return 1;
        if (
            matches.length === 2 &&
            matches.some((r) => r.state === materialStateEnum.NEW) &&
            matches.some((r) => r.state === materialStateEnum.REMOVE)
        )
            return 1;

        return matches.length;
    };

    const showCheckbox = activity.stepMode === stepModeEnum.REPORT || activity.stepMode === stepModeEnum.VALIDATE;

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell dropId={{ lineId: activity._id }} moveCell={handleMove} itemType={itemType} disabled={disabled}>
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addRental'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyRentals.map((rental, index) => {
                            const duplicateCount = countDuplicates(rental.designation, rental.rentalTypeId._id);

                            return (
                                <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                    <DraggableCell
                                        dragIds={{ itemId: rental._id, lineId: activity._id }}
                                        index={index}
                                        itemType={itemType}
                                        onDelete={() => handleDelete(rental._id)}
                                        disabled={disabled}
                                        state={rental.state}
                                        warning={duplicateCount > 1}
                                        warningChar={duplicateCount.toString()}
                                        onDuplicate={() => handleDuplicate(rental)}
                                        onDoubleClick={() => openModal(rental)}
                                    >
                                        <div className="one-line-container">
                                            <div className="one-line" title={`${rental.designation} - ${rental.rentalTypeId.designation}`}>
                                                {rental.designation} - {rental.rentalTypeId.designation}
                                            </div>
                                            {activity.stepMode === stepModeEnum.VALIDATE && (
                                                <div className="one-line" title={rental.rentalTypeId.codeProC}>
                                                    {rental.rentalTypeId.codeProC}
                                                </div>
                                            )}
                                            {rental.observations && <div className="one-line">{rental.observations}</div>}
                                        </div>
                                    </DraggableCell>

                                    {showCheckbox && (
                                        <div className="h-25px fx-r fx-items-c" title={capitalizeFirstLetter(t('isUsed'))}>
                                            <Checkbox
                                                checked={rental.isUsed}
                                                onChange={(_, checked) => {
                                                    updateDailyRental(
                                                        { id: rental._id, body: { isUsed: checked } },
                                                        {
                                                            onSuccess: () => {
                                                                refetch();
                                                            },
                                                        }
                                                    );
                                                }}
                                                size="small"
                                                color="success"
                                                sx={{ padding: 0 }}
                                                disabled={disabled || !activity.includedInExpenses}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && (
                <ManageDailyRentalModal
                    open={manageModal.value}
                    handleClose={manageModal.setFalse}
                    refetch={refetch}
                    dailyRental={selectedItem}
                    activity={activity}
                    createTransfer={createTransfer}
                />
            )}
        </Box>
    );
};

export default RentalsCell;
